import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";
import { fadeUp, stagger } from "@/lib/motion";

const TESTIMONIALS = [
  {
    quote:
      "Migrating our multi-godown inventory to TallyPrime was seamless. The team handled everything — from data cleanup to team training.",
    name: "Priya Sharma",
    role: "CFO · Sharma Textiles",
    avatar: "https://images.pexels.com/photos/7580837/pexels-photo-7580837.jpeg?auto=compress&cs=tinysrgb&w=400",
    rating: 5,
  },
  {
    quote:
      "Their support team responds in minutes. GST returns and e-invoicing became one-click for us — a huge win for our accounts department.",
    name: "Rohan Mehta",
    role: "Founder · Mehta Retail Pvt Ltd",
    avatar: "https://images.pexels.com/photos/7580994/pexels-photo-7580994.jpeg?auto=compress&cs=tinysrgb&w=400",
    rating: 5,
  },
  {
    quote:
      "We deployed TallyPrime on cloud for 3 branches. Zero downtime, complete audit trail — the ROI has been remarkable.",
    name: "Ananya Iyer",
    role: "Finance Head · Iyer & Co. Chartered Accountants",
    avatar: "https://images.pexels.com/photos/7654445/pexels-photo-7654445.jpeg?auto=compress&cs=tinysrgb&w=400",
    rating: 5,
  },
];

const STATS = [
  { value: "1500+", label: "Businesses served" },
  { value: "20+", label: "Years experience" },
  { value: "1hr", label: "avg. response SLA" },
  { value: "4.9/5", label: "Customer rating" },
];

export default function Testimonials() {
  return (
    <section data-testid="testimonials-section" className="relative py-24 lg:py-32 bg-[#0A2E6B] text-white overflow-hidden grain circuit-pattern">
      <div className="orb bg-[#FFA000] w-[500px] h-[500px] top-0 -left-40 opacity-15" />
      <div className="orb bg-[#2196F3] w-[400px] h-[400px] bottom-0 -right-32 opacity-10" style={{ animationDelay: "-5s" }} />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          variants={stagger(0.1, 0.08)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          className="max-w-2xl"
        >
          <motion.span variants={fadeUp} className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-[11px] font-bold tracking-[0.14em] uppercase text-white/85 border border-white/15 bg-white/5">
            <span className="w-1.5 h-1.5 rounded-full bg-[#FFA000] shadow-[0_0_0_4px_rgba(255,160,0,0.2)]" /> Testimonials
          </motion.span>
          <motion.h2 variants={fadeUp} className="mt-6 font-display font-black text-4xl sm:text-5xl leading-[1.08] tracking-tight text-white">
            Loved by finance{" "}
            <span className="font-serif-accent text-[#FFA000]">teams across India.</span>
          </motion.h2>
        </motion.div>

        <motion.div
          variants={stagger(0.08, 0.1)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5"
        >
          {TESTIMONIALS.map((t, i) => (
            <motion.div
              key={t.name}
              variants={fadeUp}
              whileHover={{ y: -6 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`testimonial-card-${i}`}
              className="relative rounded-2xl p-7 bg-[#0D47A1]/60 border border-white/10"
            >
              <Quote className="w-9 h-9 text-[#FFA000]" strokeWidth={1.5} />
              <p className="mt-5 text-[16px] leading-relaxed text-white/85">"{t.quote}"</p>

              <div className="mt-6 flex items-center gap-3">
                <img src={t.avatar} alt={t.name} loading="lazy" className="w-11 h-11 rounded-full object-cover ring-2 ring-white/20" />
                <div>
                  <div className="font-display font-bold text-white text-[15px]">{t.name}</div>
                  <div className="text-[12px] text-white/60">{t.role}</div>
                </div>
                <div className="ml-auto flex items-center gap-0.5">
                  {[...Array(t.rating)].map((_, s) => (
                    <Star key={s} className="w-3.5 h-3.5 fill-[#FFA000] text-[#FFA000]" />
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Stats bar */}
        <motion.div
          variants={stagger(0.06, 0.08)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 pt-12 border-t border-white/10"
        >
          {STATS.map((s) => (
            <motion.div key={s.label} variants={fadeUp} data-testid={`stat-${s.label.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="font-display font-black text-4xl sm:text-5xl tracking-tight text-white">{s.value}</div>
              <div className="mt-1 text-[13px] text-white/60">{s.label}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
